/**
 * `MastraFactory` — the single entry point to the whole Mastra Software Factory.
 *
 * The consumer's deploy entry constructs deployment-specific config instances
 * (auth adapter, pubsub) and passes them here explicitly. The only provider
 * defaults constructed here are Platform GitHub, incident.io, and Linear
 * integrations when Platform credentials exist and the caller did not provide
 * those integrations.
 *
 * `prepare()` resolves feature readiness, threads every dependency explicitly,
 * assembles the web routes/middleware, and returns the constructor args for
 * `new Mastra(...)`. The literal `export const mastra = new Mastra(...)` must
 * stay in the entry file — the deployer's `checkConfigExport` Babel plugin
 * only marks the config valid when it finds that literal in the entry AST —
 * so the factory produces args instead of the instance. `finalize()` runs the
 * post-construct boot (controller init + workers).
 *
 * Integration readiness is derived from each instance's declared capabilities
 * and the storage domains those capabilities require.
 */

import { MastraAuthStudio } from '@mastra/auth-studio';
import { prepareAgentControllerMount } from '@mastra/code-sdk';
import type { MastraCodeState } from '@mastra/code-sdk/schema';
import type { AgentControllerRequestContext } from '@mastra/core/agent-controller';
import { AgentControllerChannels } from '@mastra/core/channels';
import { EventEmitterPubSub } from '@mastra/core/events';
import type { PubSub } from '@mastra/core/events';
import type { Mastra } from '@mastra/core/mastra';
import type { RequestContext } from '@mastra/core/request-context';
import { hasAuthInit, isUserProvider } from '@mastra/core/server';
import type { IMastraAuthProvider } from '@mastra/core/server';
import type { FactoryStorage } from '@mastra/core/storage';
import type { MastraVector } from '@mastra/core/vector';
import type { WorkspaceSandbox } from '@mastra/core/workspace';
import {
  buildAuthRoutes,
  createFactoryAuthGate,
  createFactoryRouteAuth,
  getFactoryAuthOrgId,
  getFactoryAuthUserFromContext,
  getFactoryAuthUserId,
} from './auth.js';
import { createBoardRegistry, workItemPhaseSemantics } from './boards/index.js';
import type { BoardRegistry, InstalledBoard } from './boards/index.js';
import { touchFeed } from './feed-events.js';
import type { FactoryIntegration, IntegrationPostToolContext, IntegrationTools } from './integrations/base.js';
import { reconcileGithubAcceptanceLabels } from './integrations/github/acceptance-labels.js';
import type { GithubIntegration } from './integrations/github/integration.js';
import {
  recordFactoryPullRequestProvenance,
  resolveFactoryPullRequestParentWorkItemId,
} from './integrations/github/provenance.js';
import type { FactoryPullRequestProvenanceData } from './integrations/github/provenance.js';
import { isValidGitRef } from './integrations/github/sandbox.js';
import { PlatformGithubIntegration } from './integrations/platform/github/integration.js';
import { PlatformIncidentioIntegration } from './integrations/platform/incidentio/integration.js';
import { PlatformLinearIntegration } from './integrations/platform/linear/integration.js';
import { createCustomProvidersPrimer, registerCustomProvidersSource } from './routes/custom-provider-source.js';
import { ProjectRoutes } from './routes/projects.js';
import { assembleFactoryApiRoutes, buildIntegrationContext } from './routes/surface.js';
import type { FactoryApiRoutesDeps } from './routes/surface.js';
import { TelemetryRoutes } from './routes/telemetry.js';
import {
  createTenantCredentialPrimer,
  primeTenantCredentials,
  registerTenantCredentialResolver,
} from './routes/tenant-credentials.js';
import { FactoryDecisionDispatcher } from './rules/dispatcher.js';
import { FactoryPhaseStateProcessor } from './rules/processor.js';
import { createTerminalStageCleanup } from './rules/terminal-cleanup.js';
import { createFactoryTransitionTools } from './rules/tools.js';
import { FactoryTransitionService } from './rules/transition-service.js';
import { assertFactoryConfigVersion, DEFAULT_FACTORY_CONFIG_VERSION } from './rules/validation.js';
import { SessionRetirementCoordinator } from './sandbox/session-retirement.js';
import type { MastraFactorySandboxConfig } from './sandbox/session-sandbox.js';
import { createPlaintextFactorySecretEncryption } from './secret-encryption.js';
import type { FactorySecretEncryption } from './secret-encryption.js';
import { handleServerError } from './server-error.js';
import { refreshFactorySessionMemorySettings } from './session/factory-session.js';
import { observeSessionFilesystem } from './session/filesystem-capture.js';
import { observeSessionFirstExec } from './session/first-exec-capture.js';
import { observeSessionFirstMessage } from './session/first-message-capture.js';
import { LiveSessions } from './session/live-sessions.js';
import { hydrateSessionMemorySettings } from './session/memory-settings-hydration.js';
import { hydrateSessionModelPack } from './session/model-pack-hydration.js';
import { observeSessionRunEnd } from './session/run-audit.js';
import { observeSessionThreadTitle } from './session/thread-title-mirror.js';
import { createSpaStaticMiddleware, resolveUiDistDir } from './spa-static.js';
import { createStateSigner } from './state-signing.js';
import { observeAgentGitAction } from './storage/domains/audit/agent-audit.js';
import { AuditStorage } from './storage/domains/audit/base.js';
import { AuditDomain } from './storage/domains/audit/domain.js';
import { ChannelIdentityStorage } from './storage/domains/channel-identity/base.js';
import { WorkItemCommentsStorage } from './storage/domains/comments/base.js';
import { CommentsDomain } from './storage/domains/comments/domain.js';
import { FactoryFeedReader } from './storage/domains/comments/feed-context.js';
import type { WorkItemFeedPublisher } from './storage/domains/comments/feed-sync.js';
import { ModelCredentialsStorage } from './storage/domains/credentials/base.js';
import { CustomProvidersStorage } from './storage/domains/custom-providers/base.js';
import { FilesystemStorage } from './storage/domains/filesystem/base.js';
import { IntakeStorage } from './storage/domains/intake/base.js';
import { IntegrationStorage } from './storage/domains/integrations/base.js';
import { MemorySettingsStorage } from './storage/domains/memory-settings/base.js';
import { ModelPacksStorage } from './storage/domains/model-packs/base.js';
import { FactoryProjectsStorage } from './storage/domains/projects/base.js';
import { QueueHealthStorage } from './storage/domains/queue-health/base.js';
import { SourceControlStorage } from './storage/domains/source-control/base.js';
import { WorkItemsStorage } from './storage/domains/work-items/base.js';
import type { WorkItemRow } from './storage/domains/work-items/base.js';
import { FactorySupervisorHealthWorker } from './supervisor/health-worker.js';
import { SUPERVISOR_INSTRUCTIONS } from './supervisor/instructions.js';
import { createFactorySupervisorReadTools } from './supervisor/read-tools.js';
import { hydrateSupervisorSession, parseSupervisorResourceId, resolveSupervisorScope } from './supervisor/session.js';
import { createFactorySupervisorWriteTools } from './supervisor/write-tools.js';
import { timedPhase } from './timing.js';
import { createWorkspaceFactory, FactoryWorkspaceRegistry } from './workspace.js';
import type { FactorySandboxStart } from './workspace.js';

type BuildApiRoutesDeps = Pick<FactoryApiRoutesDeps, 'controller' | 'authStorage'>;

/** Constructor args for the `new Mastra(...)` literal in the deploy entry. */
export type MastraArgs = NonNullable<ConstructorParameters<typeof Mastra>[0]>;

export interface MastraFactoryConfig {
  /**
   * Auth provider instance — `MastraAuthStudio` (`@mastra/auth-studio`),
   * `MastraAuthWorkos` (`@mastra/auth-workos`), `MastraAuthBetterAuth`
   * (`@mastra/auth-better-auth`), or any custom `MastraAuthProvider`. Whatever
   * instance is passed is the active provider; a passed instance is always
   * honored as-is.
   *
   * Omitted → the factory defaults to `MastraAuthStudio`, proxying auth
   * through the shared Mastra platform API. `MastraAuthStudio` resolves its
   * own env (`MASTRA_SHARED_API_URL`, `MASTRA_ORGANIZATION_ID`,
   * `MASTRA_COOKIE_DOMAIN`).
   *
   * Pass `null` to disable auth entirely (open server, local-dev behavior)
   * without falling back to the default.
   */
  auth?: IMastraAuthProvider | null;
  /**
   * REQUIRED. Factory storage backend powering BOTH agent storage (threads,
   * messages, memory, OM — via `getMastraStorage()`) and the app tables
   * (projects/source-control/audit/intake — via the generic ops surface). Pass a
   * `PgFactoryStorage` (`@mastra/pg`) for deployments or a
   * `LibSQLFactoryStorage` (`@mastra/libsql`) for local dev — one backend,
   * one connection, every feature on.
   */
  storage: FactoryStorage;
  /**
   * Vector store instance for recall search — `PgVector` (`@mastra/pg`) on
   * the same database as `storage`. Omitted → the SDK mount's default vector
   * store resolution applies.
   */
  vector?: MastraVector;
  /**
   * Distributed event bus instance (e.g. `new RedisStreamsPubSub({ url })`).
   * When set, streams/workflows/signals ride it across processes and the
   * controller drops file-based thread locks in favor of pubsub-coordinated
   * leases. Omitted → in-process default.
   */
  pubsub?: PubSub;
  /**
   * Browser-facing origin used to build integration OAuth/install callback
   * URLs and to derive the auth redirect URI. On the platform the SPA is
   * hosted separately, so this MUST be the public API origin.
   * Default: `http://localhost:4111` (the local Factory server, which also
   * serves the UI).
   */
  publicUrl?: string;
  /**
   * Allowed cross-origin SPA origins. The SPA may be served from a separate
   * static host, so credentialed requests must be explicitly allowed.
   */
  allowedOrigins?: string[];
  /** Sandbox configuration. Omitted → repository sandboxes are disabled. */
  sandbox?: MastraFactorySandboxConfig;
  /**
   * When a session's sandbox boots: on the agent's first command (`'lazy'`,
   * the default) or as soon as the session's workspace is first resolved
   * (`'eager'`), so the boot overlaps the model's own latency.
   */
  sandboxStart?: FactorySandboxStart;
  /** Background Factory dispatcher configuration. */
  dispatcher?: MastraFactoryDispatcherConfig;
  /**
   * Deployment-stable secret for signing integration OAuth `state` values.
   * Omitted → a per-process random secret, which is fine for single-process
   * local development but rejected for integrations that declare
   * `requiresStableStateSigner`.
   */
  stateSecret?: string;
  /**
   * Encryption boundary for persisted model credentials, custom-provider API
   * keys, integration connections, and integration settings. Strongly
   * recommended whenever auth is enabled — omitting it falls back to explicit
   * plaintext compatibility with a boot-time warning.
   */
  secretEncryption?: FactorySecretEncryption;
  /**
   * Registered capability providers. The factory registers the pieces each
   * `FactoryIntegration` instance provides — HTTP routes, storage domains,
   * agent/session tools, intake, source control, and diagnostics — into the
   * system. When Platform credentials are configured, missing `github` and
   * `linear` integrations default to their Platform-backed implementations.
   */
  integrations?: FactoryIntegration[];
  /**
   * Operator-maintained provenance label stamped on transition audit rows,
   * deferred decisions, and session kickoff headers so a row can be traced
   * back to the deployment that produced it. Nothing branches on it.
   * Default: `factory-config-v1`.
   */
  configVersion?: string;
  /** Board definitions installed for this Factory instance. */
  boards?: readonly InstalledBoard[];
  /** Whether the built-in Work and Review boards are installed. Default: true. */
  includeDefaultBoards?: boolean;

  /**
   * Platform-specific overrides. `githubAppSlug` identifies Factory's own
   * GitHub App writes so their webhook deliveries do not retrigger triage.
   */
  platform?: {
    githubAppSlug?: string;
  };
}

export type { MastraFactorySandboxConfig } from './sandbox/session-sandbox.js';
export type { FactorySandboxStart } from './workspace.js';

/**
 * Per-process cap on concurrent background Factory dispatches. Omitted means
 * the dispatcher default; this is a local replica budget, not a global queue
 * limit shared across deployments.
 */
export interface MastraFactoryDispatcherConfig {
  maxInFlight?: number;
}

const CONTROLLER_ID = 'code';

function hasPlatformCredentials(): boolean {
  // MASTRA_PLATFORM_ACCESS_TOKEN is the credential Mastra Platform injects
  // into deployed projects; MASTRA_PLATFORM_SECRET_KEY is the org secret key
  // written by project scaffolding. Either enables the platform integrations.
  return Boolean(process.env.MASTRA_PLATFORM_ACCESS_TOKEN?.trim() || process.env.MASTRA_PLATFORM_SECRET_KEY?.trim());
}

/**
 * Default auth provider — `MastraAuthStudio`, which proxies identity to the
 * shared Mastra platform API. `MastraAuthStudio` resolves `MASTRA_SHARED_API_URL`,
 * `MASTRA_ORGANIZATION_ID`, and `MASTRA_COOKIE_DOMAIN` from env on its own —
 * this helper only derives a cookie-domain fallback from the factory's
 * `publicUrl`.
 *
 * Cookie-domain resolution (Studio picks the first that wins):
 *   1. explicit `MASTRA_COOKIE_DOMAIN` env, if set;
 *   2. `.mastra.ai` when `sharedApiUrl` is on `.mastra.ai`;
 *   3. this parent-domain fallback derived from `publicUrl` — so a deploy on
 *      `https://foo.mastra.cloud` mints cookies with `Domain=.mastra.cloud`
 *      without the caller wiring the env var by hand.
 *   4. otherwise host-only (no `Domain=`), which is correct for `localhost`.
 */
function buildDefaultStudioAuth(publicUrl: string): IMastraAuthProvider {
  return new MastraAuthStudio({
    cookieDomain: parentDomainFromPublicUrl(publicUrl),
  });
}

/**
 * Derive a parent cookie domain from `publicUrl` by stripping the leftmost
 * label — the same shape platform-API's env injection uses (see
 * `platform/servers/api/src/lib/studio-env-vars.ts`: `.${routingDomain.replace(/^[^.]+\./, '')}`).
 *
 * Rather than a generic `strip-left-label` heuristic — which either emits
 * cookies scoped to a public suffix (`sub.example.co.uk` → `.example.co.uk`
 * requires PSL data to be safe) or misclassifies numeric hostnames like
 * `3scale.example.com` as IPv4 — we only derive a parent domain when the
 * host sits under one of the platform's known registrable domains. Anything
 * else (custom domains, arbitrary tenant hostnames, IPs, `localhost`)
 * falls through to host-only cookies. Callers that need a different scope
 * pass `MASTRA_COOKIE_DOMAIN` explicitly (Studio honors that first).
 */
const KNOWN_PLATFORM_COOKIE_PARENTS = ['mastra.cloud', 'mastra.ai'] as const;

function isIpLiteral(hostname: string): boolean {
  // IPv6 addresses in URLs are bracketed; `URL.hostname` strips the brackets
  // but the address itself still contains `:`. IPv4 is four dot-separated
  // numeric octets — trust the parser to have already validated shape.
  if (hostname.includes(':')) return true;
  return /^\d+(?:\.\d+){3}$/.test(hostname);
}

function parentDomainFromPublicUrl(publicUrl: string): string | undefined {
  let hostname: string;
  try {
    hostname = new URL(publicUrl).hostname.toLowerCase();
  } catch {
    return undefined;
  }
  if (hostname === 'localhost' || isIpLiteral(hostname)) return undefined;
  for (const parent of KNOWN_PLATFORM_COOKIE_PARENTS) {
    // Exact match → we're already on the parent, host-only is correct.
    // Subdomain match → mint the parent-scoped cookie.
    if (hostname === parent) return undefined;
    if (hostname.endsWith(`.${parent}`)) return `.${parent}`;
  }
  return undefined;
}

/** A session parking or being answered is inbox news, so the registry bumps the project's feed. */
function liveSessionsTouchingTheFeed(controller: BuildApiRoutesDeps['controller'], eventBus: PubSub): LiveSessions {
  const liveSessions = new LiveSessions(controller);
  liveSessions.onParkedChanged(session => {
    const { factoryOrgId, factoryProjectId } = session.state.get();
    if (factoryOrgId && factoryProjectId) touchFeed(eventBus, { orgId: factoryOrgId, factoryProjectId });
  });
  return liveSessions;
}

export class MastraFactory {
  readonly #config: MastraFactoryConfig;
  readonly #boards: BoardRegistry;
  readonly #configVersion: string;
  #prepared: Awaited<ReturnType<typeof prepareAgentControllerMount>> | undefined;
  #dispatcher: FactoryDecisionDispatcher | undefined;
  #factoryProcessor: FactoryPhaseStateProcessor | undefined;
  #preparing = false;

  constructor(config: MastraFactoryConfig) {
    if (!config?.storage) {
      throw new Error(
        "MastraFactory: 'storage' is required. Pass a FactoryStorage backend — e.g. " +
          "new PgFactoryStorage({ connectionString }) from '@mastra/pg' for deployments, or " +
          "new LibSQLFactoryStorage({ url }) from '@mastra/libsql' for local dev.",
      );
    }
    this.#boards = createBoardRegistry({
      boards: config.boards,
      includeDefaultBoards: config.includeDefaultBoards,
    });
    if ('rules' in config) {
      throw new Error(
        "MastraFactory: 'rules' was removed. Set 'configVersion' for the audit label and declare tool-result " +
          'rules on the owning board via defineBoard({ tools }).',
      );
    }
    this.#configVersion = assertFactoryConfigVersion(config.configVersion ?? DEFAULT_FACTORY_CONFIG_VERSION);
    this.#config = config;
  }

  /**
   * Resolve feature readiness, wire every dependency explicitly, and assemble
   * everything needed to construct the server-owned Mastra. Returns the args
   * for the `new Mastra(...)` literal that must live in the entry file.
   */
  async prepare(): Promise<MastraArgs> {
    // Guard set synchronously (before the first await) so overlapping calls —
    // not just strictly sequential ones — can't double-seed the runtime
    // registry or double-run one-time adapter init.
    if (this.#preparing) throw new Error('MastraFactory.prepare() called twice');
    this.#preparing = true;

    const publicOrigin = (this.#config.publicUrl ?? 'http://localhost:4111').replace(/\/+$/, '');
    const allowedOrigins = (this.#config.allowedOrigins ?? []).map(o => o.replace(/\/+$/, '')).filter(Boolean);
    const storage = this.#config.storage;
    const vector = this.#config.vector;
    const pubsub = this.#config.pubsub;
    // One bus for feed/attention events: a second fallback instance would leave
    // SSE subscribers and producers on different emitters in single-process runs.
    const eventBus = pubsub ?? new EventEmitterPubSub();
    // Default auth: honor an explicitly-passed provider (including `null` to
    // disable auth) as-is; otherwise fall back to `MastraAuthStudio`
    // (platform-proxied identity). The default derives its cookie domain
    // from `publicUrl` — deploys on `<sub>.mastra.cloud` mint parent-domain
    // cookies without the caller wiring `MASTRA_COOKIE_DOMAIN` explicitly.
    const configuredAuth = this.#config.auth;
    const auth: IMastraAuthProvider | undefined =
      configuredAuth === null ? undefined : (configuredAuth ?? buildDefaultStudioAuth(publicOrigin));
    if (auth && !this.#config.secretEncryption) {
      console.warn(
        "[factory] auth is enabled but 'secretEncryption' is not configured. Persisted model credentials, " +
          'custom-provider API keys, and integration secrets will be stored as plaintext. Provide ' +
          "'secretEncryption' (e.g. via FACTORY_CREDENTIAL_ENCRYPTION_KEY) to encrypt them at rest.",
      );
    }
    const secretEncryption = this.#config.secretEncryption ?? createPlaintextFactorySecretEncryption();
    // One RouteAuth seam per boot, closed over the resolved provider. Every
    // factory route module receives this handle — no service locator.
    const routeAuth = createFactoryRouteAuth(auth);

    // Explicit integrations win. Platform credentials fill only missing
    // provider slots so callers can override each integration independently.
    const integrations = [...(this.#config.integrations ?? [])];
    if (hasPlatformCredentials()) {
      if (!integrations.some(integration => integration.id === 'github')) {
        integrations.push(new PlatformGithubIntegration({ slug: this.#config.platform?.githubAppSlug }));
      }
      if (
        process.env.MASTRA_INCIDENT_IO_CONNECTION_ID?.trim() &&
        !integrations.some(integration => integration.id === 'incidentio')
      ) {
        integrations.push(new PlatformIncidentioIntegration());
      }
      if (!integrations.some(integration => integration.id === 'linear')) {
        integrations.push(new PlatformLinearIntegration());
      }
    }

    // Validate ids up front so a copy-paste duplicate fails loud instead of one
    // instance silently shadowing the other.
    const integrationIds = new Set<string>();
    for (const integration of integrations) {
      if (integrationIds.has(integration.id)) {
        throw new Error(`MastraFactory: duplicate integration id '${integration.id}' in 'integrations'.`);
      }
      integrationIds.add(integration.id);
    }
    const configVersion = this.#configVersion;

    // FactoryStorage owns every app-table domain and initializes them through
    // the same lifecycle as the backend connection.
    const intakeStorage = storage.registerDomain(new IntakeStorage());
    const auditStorage = storage.registerDomain(new AuditStorage());
    const workItemsStorage = storage.registerDomain(new WorkItemsStorage());
    workItemsStorage.onAttentionChanged(scope => touchFeed(eventBus, scope));
    workItemsStorage.useTerminalPhasePredicate(item => workItemPhaseSemantics(this.#boards, item)?.kind === 'terminal');
    const modelCredentialsStorage = storage.registerDomain(new ModelCredentialsStorage(secretEncryption));
    const modelPacksStorage = storage.registerDomain(new ModelPacksStorage());
    const memorySettingsStorage = storage.registerDomain(new MemorySettingsStorage());
    const customProvidersStorage = storage.registerDomain(new CustomProvidersStorage(secretEncryption));
    const queueHealthStorage = storage.registerDomain(new QueueHealthStorage());
    // Generic integration storage (connections/subscriptions/settings) — the
    // default persistence surface for integrations without a bespoke domain.
    const integrationStorage = storage.registerDomain(new IntegrationStorage(secretEncryption));
    const factoryProjectsStorage = storage.registerDomain(new FactoryProjectsStorage());
    const filesystemStorage = storage.registerDomain(new FilesystemStorage());
    const sourceControlStorage = storage.registerDomain(new SourceControlStorage());
    // Reverse index from a platform sender (Slack/Discord/...) to a Mastra
    // tenant, so inbound channel events can resolve the sender's model creds.
    const channelIdentityStorage = storage.registerDomain(new ChannelIdentityStorage());
    const workItemCommentsStorage = storage.registerDomain(new WorkItemCommentsStorage());
    // Every app-table domain handle the route builders and integrations need,
    // threaded explicitly (no service locator).
    const domains = {
      intake: intakeStorage,
      modelCredentials: modelCredentialsStorage,
      modelPacks: modelPacksStorage,
      memorySettings: memorySettingsStorage,
      customProviders: customProvidersStorage,
      filesystem: filesystemStorage,
      projects: factoryProjectsStorage,
      queueHealth: queueHealthStorage,
      workItems: workItemsStorage,
      channelIdentity: channelIdentityStorage,
      comments: workItemCommentsStorage,
    };
    const auditDomain = new AuditDomain({
      auth: routeAuth,
      audit: auditStorage,
      projects: factoryProjectsStorage,
      users: auth && isUserProvider(auth) ? auth : undefined,
      sinks: integrations,
      agentTenant: requestContext => {
        const user = getFactoryAuthUserFromContext(requestContext);
        return { orgId: getFactoryAuthOrgId(user), userId: getFactoryAuthUserId(user) };
      },
    });
    // Held by reference: the channel attach below pushes into it, long after this.
    const feedPublishers: WorkItemFeedPublisher[] = [];
    const commentsDomain = new CommentsDomain({
      auth: routeAuth,
      comments: workItemCommentsStorage,
      workItems: workItemsStorage,
      projects: factoryProjectsStorage,
      channelIdentity: channelIdentityStorage,
      audit: auditDomain,
      publishers: feedPublishers,
      pubsub: eventBus,
    });

    // The sandbox config is a bare callback constructing a session's sandbox
    // from intent. Shape-only validation: probing it with a synthetic ctx at
    // boot would construct against a fake session, so only the type is
    // checked.
    const sandboxConfig = this.#config.sandbox;
    if (sandboxConfig !== undefined && typeof sandboxConfig !== 'function') {
      // An object here is almost certainly the pre-callback config, which
      // described a fleet the factory managed itself. That fleet is gone:
      // sandboxes are per session and the host constructs them, so say what to
      // write instead rather than only naming the expected type.
      if (typeof sandboxConfig === 'object' && sandboxConfig !== null) {
        throw new Error(
          `MastraFactory: 'sandbox' is now a callback, not an options object. It receives a FactorySandboxContext and returns a MastraSandbox, so the host chooses the provider per session:\n` +
            `  sandbox: ctx => new E2BSandbox({ id: ctx.sessionId })\n` +
            `The old options map three ways: 'machine' becomes the provider instance you construct inside the callback (one per session instead of one cloned template); 'workdir' is gone — remote providers clone into the VM's home directory and local providers check out under their own workingDirectory; 'maxSandboxes' is gone with the sandbox fleet — there is one sandbox per session and no pool to cap. Omit 'sandbox' entirely to disable sandboxes.`,
        );
      }
      throw new Error(
        `MastraFactory: 'sandbox' must be a function constructing a MastraSandbox from a FactorySandboxContext.`,
      );
    }

    const workspaceRegistry = new FactoryWorkspaceRegistry();

    // One shared OAuth state signer per boot. The deploy entry supplies a
    // replica-stable secret when needed; otherwise local development gets a
    // per-process random signer (`stable: false`).
    const stateSigner = createStateSigner(this.#config.stateSecret);

    // One-time provider initialization with factory-level context (e.g.
    // better-auth builds its default instance on the backend's auth
    // database, WorkOS derives its redirect URI from the public URL).
    // Failures surface here, at prepare() — a misconfigured provider must
    // not boot.
    if (auth && hasAuthInit(auth)) {
      await timedPhase('prepare.auth.init', () =>
        auth.init({ database: storage.authDatabase?.(), publicUrl: publicOrigin, allowedOrigins }),
      );
    }

    // Single init path: backend connection failure is a hard boot error;
    // registered app domains initialize fail-soft inside FactoryStorage.
    await timedPhase('prepare.storage.init', () => storage.init());

    // Per-tenant model credentials: once the credentials domain is up, model
    // resolution goes through the caller's own store and the SDK stops
    // mirroring stored API keys into process.env.
    //
    // Only register when a real auth adapter gates callers. In local /
    // auth-disabled mode there is no authenticated tenant, so registering would
    // force every model call through an empty tenant store (fail-closed, no env
    // fallback) and break chat with "Not logged in". Leaving it unregistered
    // lets the SDK fall back to the file-backed AuthStorage (auth.json) — the
    // same store the local /login and Settings pages read and write.
    if (auth) {
      registerTenantCredentialResolver(modelCredentialsStorage);
    }

    // Custom providers: DB-backed in both modes (org rows in tenant mode, the
    // sentinel `local` org in no-auth mode). Once registered, model resolution
    // and the gateway catalog never read settings.json custom providers.
    registerCustomProvidersSource({ storage: customProvidersStorage, authEnabled: Boolean(auth) });

    for (const integration of integrations) {
      integration.initialize?.({
        storage: integrationStorage.forIntegration(integration.id),
        projects: factoryProjectsStorage,
        auth: routeAuth,
      });
      if (integration.versionControl) {
        integration.versionControl.initialize({
          storage: sourceControlStorage.forIntegration(integration.id),
        });
      }
    }

    // Every integration uses generic integration storage. Version-control
    // providers additionally require the source-control storage domain. Readiness
    // is derived solely from capability presence, never from provider ids.
    const integrationRegistrations = integrations.map(integration => {
      const requiredDomains = [
        'integrations',
        ...(integration.versionControl ? ['source-control'] : []),
        // Channels resolve an inbound sender to a tenant through the reverse
        // index; without it every message would dispatch tenant-less.
        ...(integration.channels ? ['channel-identity'] : []),
      ];
      return {
        integration,
        ready: requiredDomains.every(domain => storage.isDomainReady(domain)),
        ensureReady: async () => {
          for (const domain of requiredDomains) await storage.ensureDomainReady(domain);
        },
      };
    });
    const intakeReady =
      integrations.some(integration => integration.intake !== undefined) && storage.isDomainReady('intake');
    const factoryReady = storage.isDomainReady('projects') && storage.isDomainReady('work-items');
    const knowledgeEnabled = process.env.MASTRACODE_EXPERIMENTAL_SUBCONSCIOUS === '1';
    const githubIntegration = integrations.find(integration => integration.id === 'github') as
      | GithubIntegration
      | undefined;
    const workItemsReady = storage.isDomainReady('work-items');
    const sessionRetirement =
      sandboxConfig && storage.isDomainReady('source-control')
        ? new SessionRetirementCoordinator({
            invalidateSession: sessionId => workspaceRegistry.invalidateSession(sessionId),
          })
        : undefined;
    const retireTerminalSessions =
      sessionRetirement && githubIntegration && workItemsReady
        ? async ({ orgId, workItemId }: { orgId: string; workItemId: string }) =>
            sessionRetirement.retireWorkItemSessions({
              workItems: workItemsStorage,
              sourceControl: sourceControlStorage.forIntegration(githubIntegration.id),
              orgId,
              workItemId,
            })
        : undefined;
    // Terminal-stage cleanup: ingest any trailing tool results from the item's
    // bound threads, then revoke the bindings so completed items leave the
    // reconcile walk (the active-binding set otherwise grows forever), and
    // finally release the item's sandboxes. Each step is best-effort — a
    // committed transition never fails on cleanup.
    const onTerminalStage = workItemsReady
      ? createTerminalStageCleanup({
          workItems: workItemsStorage,
          // `factoryProcessor` is assigned below in this scope; the cleanup
          // only runs on transitions long after bootstrap completes.
          reconcileBinding: async (binding): Promise<void> => {
            await factoryProcessor?.reconcileBinding(binding);
          },
          // Session retirement supersedes the older direct sandbox release: it
          // invalidates the session and stops/destroys its sandbox.
          ...(retireTerminalSessions ? { releaseSandboxes: retireTerminalSessions } : {}),
        })
      : retireTerminalSessions;
    const transitionService = workItemsReady
      ? new FactoryTransitionService({
          configVersion,
          boards: this.#boards,
          storage: workItemsStorage,
          audit: auditDomain,
          ...(onTerminalStage ? { onTerminalStage } : {}),
          ...(githubIntegration
            ? {
                onAccepted: (args: { orgId: string; factoryProjectId: string; item: WorkItemRow }) =>
                  reconcileGithubAcceptanceLabels(
                    githubIntegration,
                    sourceControlStorage.forIntegration(githubIntegration.id),
                    args,
                  ),
              }
            : {}),
        })
      : undefined;
    const projectRoutes = new ProjectRoutes({
      auth: routeAuth,
      projects: factoryProjectsStorage,
      sourceControl: sourceControlStorage,
      versionControlIntegrationIds: integrations
        .filter(integration => integration.versionControl)
        .map(integration => integration.id),
      ...(githubIntegration
        ? {
            resolveRepository: async ({ integrationId, orgId, installationId, externalId, slug }) => {
              if (integrationId !== githubIntegration.id) return null;
              const installation = await githubIntegration.sourceControlStorage.installations.get({
                orgId,
                id: installationId,
              });
              if (!installation) return null;
              const repositories = await githubIntegration.listInstallationRepos(Number(installation.externalId));
              const selected = repositories.find(repo => repo.id.toString() === externalId && repo.fullName === slug);
              if (!selected) return null;
              return githubIntegration.sourceControlStorage.repositories.upsert({
                orgId,
                input: {
                  installationId,
                  externalId,
                  slug: selected.fullName,
                  defaultBranch: isValidGitRef(selected.defaultBranch) ? selected.defaultBranch : 'main',
                  providerMetadata: { private: selected.private, owner: selected.owner },
                },
              });
            },
          }
        : {}),
      ...(sessionRetirement ? { sessionRetirement } : {}),
      ...(workItemsReady ? { workItems: workItemsStorage } : {}),
    });
    const factoryProcessor = workItemsReady
      ? new FactoryPhaseStateProcessor({
          configVersion,
          storage: workItemsStorage,
          boards: this.#boards,
          ...(transitionService ? { transitionService } : {}),
          ...(githubIntegration
            ? {
                recordPullRequestProvenance: (input: Parameters<typeof recordFactoryPullRequestProvenance>[4]) =>
                  recordFactoryPullRequestProvenance(
                    githubIntegration,
                    sourceControlStorage.forIntegration('github'),
                    integrationStorage.forIntegration('github'),
                    workItemsStorage,
                    input,
                  ),
              }
            : {}),
          messageReader: {
            listMessages: async input => {
              const memory = await storage.getMastraStorage().getStore('memory');
              return memory ? memory.listMessages(input) : { messages: [], hasMore: false };
            },
          },
        })
      : undefined;

    // Boot assertion: an active integration that signs OAuth `state` needs a
    // replica-stable signer — a per-process random secret silently breaks the
    // OAuth callback on any replica that didn't sign the state. Fail loud now
    // instead. (The built-ins also assert this inside their readiness gates.)
    for (const { integration } of integrationRegistrations) {
      if (integration.requiresStableStateSigner && !stateSigner.stable) {
        throw new Error(
          `MastraFactory: integration '${integration.id}' signs OAuth state and requires a ` +
            `replica-stable state secret, but none is configured. Set 'stateSecret' on the factory config.`,
        );
      }
    }

    // The SDK needs to know which backend the injected Mastra store uses
    // (its own `instanceof` detection breaks when the dependency graph holds
    // duplicate package copies). Resolve it by walking the FactoryStorage
    // prototype chain by class name — the factory can't import the concrete
    // classes since '@mastra/pg' / '@mastra/libsql' are the user's choice.
    const mastraStorageBackend = (() => {
      for (let proto = Object.getPrototypeOf(storage); proto; proto = Object.getPrototypeOf(proto)) {
        if (proto.constructor?.name === 'PgFactoryStorage') return 'pg' as const;
        if (proto.constructor?.name === 'LibSQLFactoryStorage') return 'libsql' as const;
      }
      return undefined;
    })();

    // Integrations contributing tools to agent sessions: org-scoped
    // `agentTools` (resolved per request) + session-scoped `sessionTools`.
    const toolIntegrations = integrationRegistrations.filter(
      ({ integration }) => integration.agentTools || integration.sessionTools,
    );

    // Build the real production controller (agents, modes, tools, memory, OM,
    // MCP, providers) — identical to the terminal app. Agent state lives in
    // the storage backend's Mastra store alongside the Factory app tables —
    // one shared database for all users, separated by `resourceId` scoping.
    const prepared = await timedPhase('prepare.controllerMount', () =>
      prepareAgentControllerMount({
        controllerId: CONTROLLER_ID,
        coAuthor: { name: 'mastra-platform[bot]' },
        workspace: createWorkspaceFactory({
          ...(sandboxConfig ? { sandbox: sandboxConfig } : {}),
          ...(this.#config.sandboxStart ? { sandboxStart: this.#config.sandboxStart } : {}),
          ...(githubIntegration ? { github: githubIntegration } : {}),
          ...(factoryProjectsStorage ? { projects: factoryProjectsStorage } : {}),
          ...(workItemsStorage ? { workItems: workItemsStorage } : {}),
          workspaceRegistry,
        }),
        disableGithubSignals: true,
        // Memory settings live in the factory's `memory-settings` app table (per
        // org/user), so the host machine's TUI settings.json must not seed them.
        disableSettingsOmSeed: true,
        hostInstructions: ({ requestContext }) => {
          const context = requestContext.get('controller') as
            | AgentControllerRequestContext<MastraCodeState>
            | undefined;
          return parseSupervisorResourceId(context?.resourceId) ? SUPERVISOR_INSTRUCTIONS : undefined;
        },
        // A factory reads the repository it works on and its skill, never the
        // ~/.claude instructions of whoever hosts the process. On the controller
        // rather than per session, so webhook-recreated sessions keep it too.
        // Blank project identity for the same reason: the SDK's defaults seed
        // sessions with the HOST process's own project root / name / branch,
        // which must never reach a hosted session's prompt. Repo-backed
        // sessions get their real workdir pinned by workspace resolution;
        // chat-only sessions legitimately have no project.
        // Factory sessions also start fail-closed on org classification: the
        // unresolved marker is set at birth, a successful org seed clears it,
        // and a resolved `factoryOrgId` always takes precedence — so a failed
        // (best-effort) seed can never leave a session classified as local.
        initialState: {
          skipGlobalInstructions: true,
          factoryOrgUnresolved: true,
          projectPath: '',
          projectName: '',
          gitBranch: '',
        },
        storage: storage.getMastraStorage(),
        ...(mastraStorageBackend ? { storageBackend: mastraStorageBackend } : {}),
        ...(factoryProcessor ? { inputProcessors: [factoryProcessor] } : {}),
        ...(vector ? { vector } : {}),
        ...(toolIntegrations.length > 0 || (workItemsStorage && transitionService)
          ? {
              extraTools: async ({ requestContext }: { requestContext: RequestContext }) => {
                const tools: IntegrationTools = {};
                const toolOwners = new Map<string, string>();
                const mergeTools = (ownerId: string, contributed: IntegrationTools) => {
                  for (const [name, tool] of Object.entries(contributed)) {
                    const owner = toolOwners.get(name);
                    if (owner) {
                      throw new Error(
                        `MastraFactory: integration tool '${name}' from '${ownerId}' conflicts with '${owner}'.`,
                      );
                    }
                    toolOwners.set(name, ownerId);
                    tools[name] = tool;
                  }
                };
                if (workItemsStorage && transitionService) {
                  mergeTools(
                    'factory',
                    await createFactoryTransitionTools({
                      requestContext,
                      storage: workItemsStorage,
                      transitionService,
                      // Heals crash-resumed sessions: recovered addresses re-seed
                      // projectRepositoryId/baseRef from the source session record.
                      // Only offered while the source-control domain is ready — a
                      // throwing lookup would abort recovery's catch block and also
                      // skip the metadata baseRef fallback.
                      ...(storage.isDomainReady('source-control')
                        ? { sessions: sourceControlStorage.forIntegration('github').sessions }
                        : {}),
                    }),
                  );
                  // The supervisor session has no seat, so it never gets the
                  // transition tool above; it gets the read surface instead,
                  // and only once the caller's org is shown to own the project.
                  const supervisorScope = await resolveSupervisorScope({
                    requestContext,
                    projects: factoryProjectsStorage,
                  });
                  if (supervisorScope) {
                    const userId = getFactoryAuthUserId(getFactoryAuthUserFromContext(requestContext));
                    mergeTools(
                      'factory-supervisor',
                      createFactorySupervisorReadTools({
                        scope: supervisorScope,
                        workItems: workItemsStorage,
                        boards: this.#boards,
                        comments: workItemCommentsStorage,
                        audit: auditStorage,
                        messageReader: {
                          listMessages: async input => {
                            const memory = await storage.getMastraStorage().getStore('memory');
                            return memory ? memory.listMessages(input) : { messages: [], hasMore: false };
                          },
                        },
                      }),
                    );
                    if (userId) {
                      mergeTools(
                        'factory-supervisor-write',
                        createFactorySupervisorWriteTools({
                          scope: supervisorScope,
                          userId,
                          workItems: workItemsStorage,
                          audit: auditDomain,
                          transitionService,
                          ...(githubIntegration
                            ? {
                                reconcileAcceptanceLabels: (args: {
                                  orgId: string;
                                  factoryProjectId: string;
                                  item: WorkItemRow;
                                }) =>
                                  reconcileGithubAcceptanceLabels(
                                    githubIntegration,
                                    sourceControlStorage.forIntegration(githubIntegration.id),
                                    args,
                                  ),
                              }
                            : {}),
                          signalSession: async ({ sessionId, message }) => {
                            const session = await prepared.base.controller.getSessionByResource(sessionId);
                            if (!session) throw new Error('The worker session is not currently available.');
                            await session.sendMessage({
                              content: message,
                              ...(requestContext ? { requestContext } : {}),
                            });
                          },
                        }),
                      );
                    }
                  }
                }
                for (const { integration, ready, ensureReady } of toolIntegrations) {
                  if (!ready && ensureReady) {
                    try {
                      await ensureReady();
                    } catch {
                      continue;
                    }
                  }
                  if (integration.agentTools) {
                    mergeTools(integration.id, await integration.agentTools({ requestContext }));
                  }
                  if (integration.sessionTools) {
                    mergeTools(integration.id, integration.sessionTools({ requestContext }));
                  }
                }
                return tools;
              },
            }
          : {}),
        postToolObserver: async (toolContext: IntegrationPostToolContext) => {
          const requestContext = (toolContext.context as { requestContext?: RequestContext } | undefined)
            ?.requestContext;
          if (requestContext) {
            await observeAgentGitAction({
              audit: auditDomain,
              toolContext: { ...toolContext, context: requestContext },
            });
          }
          await Promise.all(
            integrations.map(async integration => {
              if (!integration.postToolObserver) return;
              try {
                await integration.postToolObserver({ toolContext, requestContext });
              } catch (error) {
                console.warn(`[factory] Integration '${integration.id}' post-tool observer failed:`, error);
              }
            }),
          );
        },
        ...(pubsub ? { pubsub, crossProcessPubSub: true } : {}),
        buildApiRoutes: ({ controller, authStorage }: BuildApiRoutesDeps) => [
          // Public `/auth/*` routes (login/callback/logout/me). Folded in as
          // `apiRoutes` (not plain Hono routes) because the entry can't touch the
          // Hono app the deployer generates. `requiresAuth: false`; the gate
          // skips `/auth/*`.
          ...(auth ? buildAuthRoutes(auth, { publicUrl: publicOrigin }) : []),
          ...new TelemetryRoutes({ auth: routeAuth, providerName: auth?.name, publicOrigin, allowedOrigins }).routes(),
          // Custom `/web/*` routes (fs / config / integrations / factory / audit).
          ...assembleFactoryApiRoutes({
            controllerId: CONTROLLER_ID,
            controller,
            liveSessions: liveSessionsTouchingTheFeed(controller, eventBus),
            auth: routeAuth,
            ...(auth && isUserProvider(auth) ? { users: auth } : {}),
            authStorage,
            audit: auditDomain,
            publicOrigin,
            stateSigner,
            sandbox: sandboxConfig,
            sessionRetirement,
            factoryStorage: storage,
            integrationStorage,
            sourceControlStorage,
            domains,
            feed: commentsDomain,
            integrations: integrationRegistrations,
            intakeReady,
            factoryReady,
            knowledgeEnabled,
            configVersion,
            boardRegistry: this.#boards,
            factoryTransitionService: transitionService,
            onFactoryRuntime: ({ transitionService: runtimeTransitionService, prepareBinding }) => {
              this.#dispatcher ??= new FactoryDecisionDispatcher({
                controller,
                transitionService: runtimeTransitionService,
                storage: storage.getDomain<WorkItemsStorage>('work-items'),
                audit: auditDomain,
                boards: this.#boards,
                maxInFlight: this.#config.dispatcher?.maxInFlight,
                isAutoRunEnabled: async ({ orgId, factoryProjectId }) => {
                  await factoryProjectsStorage.ensureReady();
                  const project = await factoryProjectsStorage.get({ orgId, id: factoryProjectId });
                  return project?.autoRunEnabled ?? false;
                },
                autoApprovePlans: async ({ orgId, factoryProjectId }) => {
                  await factoryProjectsStorage.ensureReady();
                  const project = await factoryProjectsStorage.get({ orgId, id: factoryProjectId });
                  return project?.autoApprovePlans ?? false;
                },
                reconcileToolResults: () => factoryProcessor?.reconcileAllBoundThreads() ?? Promise.resolve(),
                prepareBinding,
                refreshManagedMemorySettings: ({ binding, session }) =>
                  refreshFactorySessionMemorySettings(session, {
                    orgId: binding.orgId,
                    factoryProjectId: binding.factoryProjectId,
                    projects: factoryProjectsStorage,
                    memorySettings: memorySettingsStorage,
                  }),
                feedReader: new FactoryFeedReader(workItemCommentsStorage),
                primeCredentials: tenant => primeTenantCredentials({ tenant, credentials: modelCredentialsStorage }),
                resolveLinkedWorkItemParentId: async ({ orgId, factoryProjectId, decision }) => {
                  if (decision.source !== 'github-pr') return null;
                  const repositoryId = decision.metadata?.githubRepositoryId;
                  const pullRequestNumber = decision.metadata?.githubPullRequestNumber;
                  if (typeof repositoryId !== 'number' || typeof pullRequestNumber !== 'number') return null;
                  return resolveFactoryPullRequestParentWorkItemId(
                    integrationStorage.forIntegration<
                      Record<string, unknown>,
                      Record<string, unknown>,
                      FactoryPullRequestProvenanceData
                    >('github'),
                    { orgId, factoryProjectId, repositoryId, pullRequestNumber },
                  );
                },
              });
            },
          }),
          ...projectRoutes.routes(),
          ...auditDomain.routes(),
          ...commentsDomain.routes(),
        ],
        buildServerConfig: () => {
          const cors = allowedOrigins.length ? { cors: { origin: allowedOrigins, credentials: true } } : {};
          // Log route errors with method/path/stack and answer with structured
          // JSON instead of an opaque `Internal Server Error`. Applied by the
          // deployer to both the top-level app and the custom-route sub-app.
          const onError = { onError: handleServerError };
          // Same-origin SPA: when a vite build is present (see resolveUiDistDir),
          // serve it at `/` from this server. Mounted last so the auth gate (when
          // enabled) covers it; it always passes `/api`, `/web`, `/auth` through.
          const uiDist = resolveUiDistDir();
          const spa = uiDist ? [createSpaStaticMiddleware(uiDist)] : [];
          if (!auth) {
            // Auth disabled: no gate. Still prime the sentinel-local custom
            // provider snapshot so model calls see DB rows, then SPA + CORS.
            return {
              middleware: [
                createCustomProvidersPrimer({ auth: routeAuth, storage: customProvidersStorage, authEnabled: false }),
                ...spa,
              ],
              ...cors,
              ...onError,
            };
          }

          // Ordered middleware. The deployer applies these AFTER its context
          // middleware sets `c.set('mastra', mastra)` and BEFORE routes, so:
          //   1. gate   — validates the auth session, stashes the user, and 401s /
          //               redirects unauthenticated requests. Skips public `/auth/*`.
          //   2. primers — hydrate the caller's model-credential and custom
          //               provider snapshots so the request's first model call
          //               resolves tenant credentials and custom providers.
          //   3. spa    — serves the built UI for everything the server doesn't own.
          // `auth` also lands on `server.auth` so the core auth middleware (and
          // Studio's dual-auth routing — see `studio.auth` on the returned args)
          // authenticates core `/api/*` routes with the same provider.
          return {
            auth,
            middleware: [
              createFactoryAuthGate(auth),
              createTenantCredentialPrimer({ auth: routeAuth, credentials: modelCredentialsStorage }),
              createCustomProvidersPrimer({
                auth: routeAuth,
                storage: customProvidersStorage,
                authEnabled: Boolean(auth),
              }),
              ...spa,
            ],
            ...cors,
            ...onError,
          };
        },
      }),
    );

    prepared.base.controller.onSessionCreated(session => {
      observeSessionFilesystem(session, {
        filesystem: filesystemStorage,
        sourceControl: sourceControlStorage.forIntegration('github'),
      });
      observeSessionFirstMessage(session, {
        sourceControl: sourceControlStorage.forIntegration('github'),
      });
      observeSessionFirstExec(session, {
        sourceControl: sourceControlStorage.forIntegration('github'),
      });
      observeSessionThreadTitle(session, {
        sourceControl: sourceControlStorage.forIntegration('github'),
      });
      observeSessionRunEnd(session, { audit: auditDomain });
    });

    // Supervisor sessions carry their project in the resourceId; re-stamp
    // scope, instructions and factory defaults on every (re)creation so a
    // restarted server heals the in-memory state.
    prepared.base.controller.onSessionCreated(
      session =>
        hydrateSupervisorSession(session, {
          projects: factoryProjectsStorage,
          memorySettings: memorySettingsStorage,
        }).catch(error => {
          console.warn('[Factory Supervisor] Failed to hydrate supervisor session', {
            error: error instanceof Error ? error.message : String(error),
          });
        }),
      { blocking: true },
    );

    // Blocking: `createSession` awaits this seed, so when hydration succeeds
    // a session's first run starts with the owner's stored OM settings.
    // Best-effort — failures are logged inside the helper, never thrown, and
    // the session then falls back to its persisted/default OM configuration.
    prepared.base.controller.onSessionCreated(
      session =>
        hydrateSessionMemorySettings(session, {
          sourceControl: sourceControlStorage.forIntegration('github'),
          projects: factoryProjectsStorage,
          memorySettings: memorySettingsStorage,
        }),
      { blocking: true },
    );

    // Personal model packs seed interactive user sessions only. Active Factory
    // run bindings are excluded and continue to use the project default model.
    prepared.base.controller.onSessionCreated(
      session =>
        hydrateSessionModelPack(session, {
          sourceControl: sourceControlStorage.forIntegration('github'),
          workItems: workItemsStorage,
          modelPacks: modelPacksStorage,
        }),
      { blocking: true },
    );

    this.#prepared = prepared;
    this.#factoryProcessor = factoryProcessor;

    // Chat-platform channels (Slack, Discord, …) contributed by integrations,
    // attached to the mounted controller so inbound platform messages reach
    // the same agents the web UI drives. READY integrations only — readiness
    // means the `channel-identity` domain's `init()` succeeded, so its link
    // table is queryable. Without it a sender can't be resolved to a tenant,
    // and attaching anyway would dispatch runs on default credentials.
    const channelRegistrations = integrationRegistrations.filter(
      ({ integration, ready }) => ready && integration.channels,
    );
    // `setChannels` replaces rather than merges, so a second provider would
    // silently never receive a message. Fail loud instead.
    if (channelRegistrations.length > 1) {
      throw new Error(
        `MastraFactory: integrations [${channelRegistrations
          .map(({ integration }) => integration.id)
          .join(', ')}] all provide channels, but only one may. Remove all but one.`,
      );
    }
    for (const { integration } of channelRegistrations) {
      const context = buildIntegrationContext(
        {
          controller: prepared.base.controller,
          publicOrigin,
          auth: routeAuth,
          stateSigner,
          sandbox: sandboxConfig,
          factoryStorage: storage,
          integrationStorage,
          sourceControlStorage,
          configVersion,
          boardRegistry: this.#boards,
          factoryReady,
          domains,
          feed: commentsDomain,
          ...(githubIntegration ? { sourceControlOwnerId: 'github' } : {}),
        },
        integration.id,
      );
      // Integrations return a channels CONFIG; the factory owns construction.
      prepared.base.controller.setChannels(new AgentControllerChannels(integration.channels!(context)));
      // A publisher posts through the channel SDK this loop just wired up.
      const publisher = integration.feedPublisher?.(context);
      if (publisher) feedPublishers.push(publisher);
    }

    // Integration lifecycle workers (e.g. polling an upstream without
    // webhooks): collected from READY integrations only, folded into the
    // constructor args so `new Mastra(...)` merges them with the default
    // workers and `finalize()`'s `startWorkers()` starts them alongside the
    // built-ins. Never passed for the disabled/not-ready case — a worker for
    // an unavailable integration must not run.
    const integrationWorkers = [
      ...(factoryReady
        ? [
            new FactorySupervisorHealthWorker({
              projects: factoryProjectsStorage,
              workItems: workItemsStorage,
              boards: this.#boards,
            }),
          ]
        : []),
      ...integrationRegistrations
        .filter(({ integration, ready }) => ready && integration.workers)
        .flatMap(({ integration }) =>
          integration.workers!(
            buildIntegrationContext(
              {
                controller: prepared.base.controller,
                publicOrigin,
                auth: routeAuth,
                stateSigner,
                sandbox: sandboxConfig,
                factoryStorage: storage,
                integrationStorage,
                sourceControlStorage,
                configVersion,
                boardRegistry: this.#boards,
                factoryReady,
                domains,
                feed: commentsDomain,
                ...(githubIntegration ? { sourceControlOwnerId: 'github' } : {}),
              },
              integration.id,
            ),
          ),
        ),
    ];

    return {
      ...prepared.mastraArgs,
      // Same provider on `studio.auth` as on `server.auth` (buildServerConfig):
      // deployed factories must authenticate BOTH plain API callers and Studio
      // requests (`x-mastra-client-type: studio` routes to `studio.auth`).
      ...(auth ? { studio: { auth } } : {}),
      ...(integrationWorkers.length > 0 ? { workers: integrationWorkers } : {}),
    };
  }

  /**
   * Post-construct boot: initialize the controller (which inherits the
   * constructed Mastra's storage) and start its workers. Call AFTER the entry
   * has run `new Mastra(prepare()'s args)`.
   */
  async finalize(): Promise<void> {
    if (!this.#prepared) {
      throw new Error('MastraFactory.finalize() called before prepare()');
    }
    await timedPhase('finalize.controller', () => this.#prepared!.finalize());
    await timedPhase(
      'finalize.reconcileBoundThreads',
      () => this.#factoryProcessor?.reconcileAllBoundThreads() ?? Promise.resolve(),
    );
    this.#dispatcher?.start();
  }

  /** Stop Factory-owned background dispatch before the host process shuts down. */
  async shutdown(): Promise<void> {
    await this.#dispatcher?.stop();
  }
}
